import { Form } from "antd";
import { useWatch } from "antd/es/form/Form";
import { useCallback, useMemo, useState } from "react";
import { Point, Schedule } from "tm-shared/types";
import { matchesQuery } from "tm-shared/utils";

import { ScheduleFiltersPanelValues } from "@/domains/route";
import { useDebounce, useMultiTap } from "@/hooks";
import { useAppSelector } from "@/hooks/redux";

interface UseScheduleSelectionArgs {
    points: Record<string, Point>;
}

export const useScheduleSelection = ({ points }: UseScheduleSelectionArgs) => {
    const { currentScheduleList } = useAppSelector((s) => s.manager);

    const [filtersForm] = Form.useForm<ScheduleFiltersPanelValues>();
    const watchedFilters = useWatch([], filtersForm);

    const searchValue = watchedFilters?.search || "";
    const debouncedSearch = useDebounce(searchValue, 300);

    const [multiSelectMode, setMultiSelectMode] = useState(false);
    const [selectedSchedules, setSelectedSchedules] = useState<Schedule[]>([]);

    const enterMultiSelectMode = useMultiTap(() => setMultiSelectMode(true));

    const exitMultiSelectMode = useCallback(() => {
        setMultiSelectMode(false);
        setSelectedSchedules([]);
    }, []);

    const sortedAndFilteredSchedules = useMemo(() => {
        const { day = "0" } = watchedFilters || {};
        const searchLower = debouncedSearch.trim().toLowerCase();

        setSelectedSchedules([]);

        return [...currentScheduleList]
            .filter((schedule) => {
                if (day !== "0") {
                    const scheduleDays = schedule.days.map((d) => Number(d));
                    if (!scheduleDays.includes(Number(day))) return false;
                }

                if (searchLower) {
                    const currentPoint = points[schedule.pointId];

                    if (!currentPoint) return;

                    const matches = matchesQuery(currentPoint, searchLower, [
                        (m) => m?.market?.building?.address?.name,
                        (m) => m?.market?.contragent?.name,
                        (m) => m?.name,
                    ]);

                    if (!matches) return false;
                }

                return true;
            })
            .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));
    }, [watchedFilters, debouncedSearch, currentScheduleList, points]);

    const handleSelectSchedule = useCallback(
        (item: Schedule) => {
            if (!multiSelectMode) return;

            setSelectedSchedules((prev) => {
                const exists = prev.some((p) => p.pointId === item.pointId);
                return exists
                    ? prev.filter((p) => p.pointId !== item.pointId)
                    : [...prev, item];
            });
        },
        [multiSelectMode],
    );

    const handleSelectAll = useCallback(() => {
        setSelectedSchedules([...sortedAndFilteredSchedules]);
    }, [sortedAndFilteredSchedules]);

    const handleClearSelection = useCallback(
        () => setSelectedSchedules([]),
        [],
    );

    return {
        filtersForm,
        watchedFilters,

        multiSelectMode,
        selectedSchedules,
        sortedAndFilteredSchedules,

        enterMultiSelectMode,
        exitMultiSelectMode,

        handleSelectSchedule,
        handleSelectAll,
        handleClearSelection,
    };
};
